import type { ActiveAssetRef } from "@elaso/shared/schemas/active-assets";
import { useEffect } from "react";
import { useStore } from "zustand";
import {
	acquireActiveAsset,
	flushActiveAssets,
	releaseActiveAsset,
} from "@/lib/active-assets";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";

/**
 * Declares the stage channel of the current voice session as an active asset, so
 * `stageStates` reaches this connection over the viewer-scoped
 * `active:{assetKey}` tier instead of the whole workspace (TECH-3119).
 *
 * Scoped to the voice SESSION (`activeChannel`), not to a mounted component. The
 * grid renders only the active tab, so the stage view unmounts as soon as the
 * user navigates away, while the PiP renders `stageStates` from that moment on
 * (`pip-call-content`). A component-scoped declare would unsubscribe exactly
 * when the PiP becomes the consumer and leave it frozen. `activeChannel` spans
 * both, and its existing teardown paths, leave, channel switch, join-as-speaker
 * and workspace reset, release the declaration for free.
 *
 * Also owns the snapshot fetch, ordered AFTER the declare is acknowledged. The
 * subscription then covers every change from the snapshot onward, so no update
 * can fall between the two.
 */
export function useVoiceStageActiveAsset(): void {
	const activeChannel = useStore(uiStore, (state) => state.activeChannel);
	const channels = useStore(coreStore, (state) => state.channels);

	const stageChannelId =
		activeChannel && channels.get(activeChannel.id)?.type === "stage"
			? activeChannel.id
			: undefined;

	useEffect(() => {
		if (!stageChannelId) {
			return;
		}

		const ref: ActiveAssetRef = { type: "channel", id: stageChannelId };
		acquireActiveAsset(ref);

		let released = false;
		const fetchSnapshot = () => {
			if (!released) {
				coreStore.getState().fetchStageState(stageChannelId);
			}
		};

		// Both arms run the fetch. A rejected declare, offline or timed out, still
		// self-heals, because the bootstrap resync re-declares the full set on
		// reconnect, so the snapshot is worth having either way rather than leaving
		// the view empty.
		flushActiveAssets().then(fetchSnapshot, fetchSnapshot);

		return () => {
			released = true;
			releaseActiveAsset(ref);
		};
	}, [stageChannelId]);
}
